<template>
    <v-container fluid>
        <v-card elevation="2" class="pa-0">
            <v-card-title class="d-flex justify-space-between">
                Gestione Cashflow Previsionale
                <div class="d-flex gap-2">
                    <v-btn color="success" @click="showCharts = !showCharts" outlined class="mr-2">
                        <v-icon left>{{ showCharts ? 'table_chart' : 'show_chart' }}</v-icon>
                        {{ showCharts ? 'Tabella' : 'Grafici' }}
                    </v-btn>
                    <v-btn color="info" @click="exportToExcel" outlined :loading="exporting" class="mr-2">
                        <v-icon left>download</v-icon> Esporta Excel
                    </v-btn>
                </div>
                <div class="d-flex gap-2 pt-1">
                    <v-btn color=" primary" @click="openDialog">
                        <v-icon left>add</v-icon> Nuovo Record
                    </v-btn>
                </div>
            </v-card-title>

            <CashflowSettings :starting-balance.sync="startingBalance" :starting-date.sync="startingDate"
                @refresh="fetchData" @importaDati="importaDati" :message="message" />

            <CashflowCharts v-if="showCharts" :items="calculatedItems" :starting-balance="startingBalance"
                :starting-date="startingDate" class="mt-4" />

            <CashflowDataTable v-else :items="calculatedItems" :loading="loading" @edit="editItem"
                @duplicate="duplicateItem" @delete="deleteItem" />

            <v-divider />
            <v-row class="ma-2 pa-0" v-if="!showCharts">
                <span v-if="calculatedItems">
                    Elementi totali:
                    {{ calculatedItems.length }}
                </span>
            </v-row>

        </v-card>

        <CashflowDialog v-model="dialog" :edited-item="editedItem" :is-new="editedIndex === -1" @save="save"
            @close="close" />
    </v-container>
</template>

<script>
import * as XLSX from 'xlsx';

import * as api from "@/api/finanza/api.cashflow.js";
import CashflowSettings from "@/components/cashflow/CashflowSettings.vue";
import CashflowDataTable from "@/components/cashflow/CashflowDataTable.vue";
import CashflowDialog from "@/components/cashflow/CashflowDialog.vue";
import CashflowCharts from "@/components/cashflow/CashflowCharts.vue";

export default {
    name: "CashflowManagement",

    components: {
        CashflowSettings,
        CashflowDataTable,
        CashflowDialog,
        CashflowCharts,
    },

    data: () => ({
        dialog: false,
        loading: false,
        showCharts: false,
        exporting: false,
        startingBalance: 0,
        startingDate: new Date().toISOString().substr(0, 10),
        items: [],
        editedIndex: -1,
        editedItem: {
            id: -1,
            descrizione: "",
            data: new Date().toISOString().substr(0, 10),
            orario: null,
            tipologia: "Previsionale",
            entrata: 0,
            uscita: 0,
            note: '',
        },
        defaultItem: {
            id: -1,
            descrizione: "",
            data: new Date().toISOString().substr(0, 10),
            tipologia: "Previsionale",
            orario: null,
            entrata: 0,
            uscita: 0,
            note: '',
        },
        saveSettingsTimer: null,
        message: '',
    }),

    computed: {
        calculatedItems() {
            if (!this.items?.length) return [];

            const dateStart = new Date(this.startingDate);
            dateStart.setHours(0, 0, 0, 0);

            let tipoPriority = {
                Banca: 1,
                Reale: 2,
                Previsionale: 3
            };

            const sorted = [...this.items].sort((a, b) => {
                // 1Ordine per data
                const dateComparison = new Date(a.data) - new Date(b.data);
                if (dateComparison !== 0) return dateComparison;

                // 2Ordine per orario
                const timeA = a.orario || "00:00";
                const timeB = b.orario || "00:00";
                const timeComparison = timeA.localeCompare(timeB);
                if (timeComparison !== 0) return timeComparison;

                // Ordine per tipologia
                return (tipoPriority[a.tipologia] || 99) - (tipoPriority[b.tipologia] || 99);
            });

            let currentTotal = this.startingBalance;

            return sorted
                .filter(item => new Date(item.data) >= dateStart)
                .map(item => {
                    if (!item.escludiCalcolo)
                        currentTotal += (item.entrata || 0) - (item.uscita || 0);
                    return {
                        ...item,
                        runningTotal: currentTotal.toFixed(3)
                    };
                });
        }

    },

    watch: {
        startingDate() {
            this.debounceSaveSettings();
        },
        startingBalance() {
            this.debounceSaveSettings();
        },
    },

    created() {
        this.fetchData();
    },

    beforeDestroy() {
        if (this.saveSettingsTimer) {
            clearTimeout(this.saveSettingsTimer);
        }
    },

    methods: {
        async fetchData() {
            await Promise.all([
                this.fetchSettings(),
                this.fetchCashflowData()
            ]);
        },

        async fetchSettings() {
            try {
                const { data } = await api.getCashflowSettings();
                this.startingBalance = data.saldo;
                this.startingDate = data.dataInizio.split("T")[0];
            } catch (error) {
                console.error("Errore nel recupero parametri", error);
                this.$toast?.error("Errore nel caricamento delle impostazioni");
            }
        },

        async fetchCashflowData() {
            this.loading = true;
            try {
                const { data } = await api.getCashflow();
                this.items = data.map(item => ({
                    ...item,
                    data: item.data.split("T")[0]
                }));
            } catch (error) {
                console.error("Errore nel recupero dati", error);
                this.$toast?.error("Errore nel caricamento dei dati");
            } finally {
                this.loading = false;
            }
        },

        openDialog() {
            this.editedItem = { ...this.defaultItem };
            this.editedIndex = -1;
            this.dialog = true;
        },

        editItem(item) {
            this.editedIndex = item.id;
            this.editedItem = { ...item };
            this.dialog = true;
        },

        duplicateItem(item) {
            this.editedItem = { ...item };
            this.editedIndex = -1;
            this.dialog = true;
        },

        async deleteItem(item) {
            const confirm = await this.$confirm?.open(
                "Elimina",
                "Eliminare questo record?"
            );

            if (!confirm) return;

            try {
                const { data } = await api.deleteRecord(item.id);
                if (data !== "OK") {
                    this.$toast?.error(data);
                    return;
                }

                const index = this.items.findIndex(x => x.id === item.id);
                if (index > -1) {
                    this.items.splice(index, 1);
                }
                this.refreshData += 1;
            } catch (error) {
                console.error(error);
                this.$toast?.error("Errore durante l'eliminazione");
            }
        },

        async save(editedItem) {
            try {
                const { data } = await api.saveRecord(editedItem);
                if (data !== "OK") {
                    this.$toast.error(data);
                    return;
                }

                if (this.editedIndex > -1) {
                    const index = this.items.findIndex(x => x.id === this.editedIndex);
                    Object.assign(this.items[index], editedItem);
                } else {
                    editedItem.id = Math.max(...this.items.map(x => x.id)) + 1;
                    this.items.push({ ...editedItem });
                }
                this.close();
            } catch (error) {
                console.error(error);
                this.$toast?.error("Errore durante il salvataggio");
            }
        },

        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editedItem = { ...this.defaultItem };
                this.editedIndex = -1;
            });
        },

        debounceSaveSettings() {
            clearTimeout(this.saveSettingsTimer);
            this.saveSettingsTimer = setTimeout(async () => {
                try {
                    const settings = {
                        saldo: this.startingBalance,
                        dataInizio: this.startingDate
                    };

                    await api.saveCashflowSettings(settings);

                } catch (error) {
                    console.error("Errore nel salvataggio impostazioni", error);
                }
            }, 800);
        },

        async importaDati() {
            let res = await api.importData();
            this.message = res.data;
        },

        //#region  Esporta Excel

        async exportToExcel() {
            this.exporting = true;
            try {
                // Prepara i dati per l'export
                const exportData = this.calculatedItems.map(item => ({
                    'Data': this.formatDateForExport(item.data),
                    'Orario': item.orario || '',
                    'Descrizione': item.descrizione,
                    'Tipo': item.tipologia,
                    'Entrate (€)': item.entrata || 0,
                    'Uscite (€)': item.uscita || 0,
                    'Saldo Progressivo (€)': item.runningTotal
                }));

                // Calcola totali
                const totaleEntrate = exportData.reduce((sum, item) => sum + item['Entrate (€)'], 0);
                const totaleUscite = exportData.reduce((sum, item) => sum + item['Uscite (€)'], 0);
                const saldoFinale = exportData.length > 0 ? exportData[exportData.length - 1]['Saldo Progressivo (€)'] : 0;

                // Aggiungi riga vuota e riga di riepilogo
                exportData.push({});
                exportData.push({
                    'Data': 'TOTALE',
                    'Orario': '',
                    'Descrizione': '',
                    'Tipo': '',
                    'Entrate (€)': totaleEntrate,
                    'Uscite (€)': totaleUscite,
                    'Saldo Progressivo (€)': saldoFinale
                });

                // Aggiungi informazioni iniziali
                exportData.unshift({});
                exportData.unshift({
                    'Data': 'Saldo Iniziale',
                    'Orario': '',
                    'Descrizione': '',
                    'Tipo': '',
                    'Entrate (€)': '',
                    'Uscite (€)': '',
                    'Saldo Progressivo (€)': this.startingBalance
                });
                exportData.unshift({
                    'Data': 'Data Inizio',
                    'Orario': '',
                    'Descrizione': this.formatDateForExport(this.startingDate),
                    'Tipo': '',
                    'Entrate (€)': '',
                    'Uscite (€)': '',
                    'Saldo Progressivo (€)': ''
                });
                exportData.unshift({});

                // Crea workbook
                const ws = XLSX.utils.json_to_sheet(exportData, {
                    skipHeader: false
                });

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Cashflow");

                // Imposta larghezza colonne
                ws['!cols'] = [
                    { wch: 12 }, // Data
                    { wch: 8 },  // Orario
                    { wch: 30 }, // Descrizione
                    { wch: 15 }, // Tipo
                    { wch: 14 }, // Entrate
                    { wch: 14 }, // Uscite
                    { wch: 20 }  // Saldo Progressivo
                ];

                // Genera file
                const fileName = `Cashflow_${this.formatDateForFilename(new Date())}.xlsx`;
                XLSX.writeFile(wb, fileName);

            } catch (error) {
                console.error('Errore durante l\'esportazione', error);
                this.$toast?.error('Errore durante l\'esportazione Excel');
            } finally {
                this.exporting = false;
            }
        },

        formatDateForExport(isoDate) {
            if (!isoDate) return '';
            const [year, month, day] = isoDate.split('-');
            return `${day}/${month}/${year}`;
        },

        formatDateForFilename(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}${month}${day}_${hours}${minutes}`;
        },

        //#endregion
    }
};
</script>